'use client';
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FaqZigzag from './components/FaqZigzag';
import CtaBlock from './components/CtaBlock';
import ServicesSection from './components/ServicesSection';
import PhotoQuoteForm from './components/PhotoQuoteForm';
import StickyMobileBar from './components/StickyMobileBar';
import WaitlistSection from './components/WaitlistSection';
import ResultsPopup from './components/ResultsPopup';
import { trackPageView } from '@/lib/trackPageView';

const faqItems = [
  {
    question: 'Do I need to bring my car anywhere?',
    answer:
      "Nope — that's the whole point. Vertex Premium is fully mobile. We come to your home, workplace, or wherever your vehicle is parked in the Mesquite, NV area. All you need to do is make sure there's a water spigot nearby that we can connect to. We handle everything else: equipment, products, towels, and the elbow grease.",
    beforeSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_1202956ad-1772069931912.png',
    afterSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_1202956ad-1772069931912.png',
    beforeAlt: 'Dusty SUV parked in driveway before mobile detail service',
    afterAlt: 'Same SUV gleaming clean after Vertex Premium mobile detail service',
    microCta: 'Book Mobile Detail',
    tag: 'Mobile Service',
  },
  {
    question: "What's the difference between the Basic Bundle and the Premium Bundle?",
    answer:
      "The Basic Bundle covers your core needs — steam-cleaned seats, full interior vacuum, exterior foam wash, wax protection, and tire shine. It's a solid refresh. The Premium Bundle goes deeper: Tornador treatment of vents and crevices, iron remover, clay bar decontamination, quality sealant, and leather or fabric conditioning. If your car hasn't had a proper detail in a while, the Premium is where the real transformation happens.",
    beforeSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_10d543ad9-1772167304500.png',
    afterSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_10d543ad9-1772167304500.png',
    beforeAlt: 'Car interior with dirty seats and dusty dashboard before detailing',
    afterAlt: 'Same car interior after premium detail — clean seats, conditioned leather, spotless panels',
    microCta: 'Compare Packages',
    tag: 'Service Tiers',
  },
  {
    question: 'What is the Luxe Package and who is it for?',
    answer:
      "The Luxe Package is the full treatment — nothing is skipped. Full steam extraction of seats, carpets, and floor mats. Complete Tornador treatment of every vent, crevice, and seat belt. Multi-stage hand wash with iron remover and full clay bar decontamination. Wheels cleaned front and back with wheel well dressing. Paint hand-enhanced with a high-grade sealant. Exterior trim, plastics, and exhaust tips dressed. Every door jamb, fuel door, and piece of glass finished streak-free. It's for people who want their car to feel brand new.",
    beforeSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_1862b0966-1772167304016.png',
    afterSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_1862b0966-1772167304016.png',
    beforeAlt: 'Truck exterior with dull paint, dirty wheels, and grimy trim before Luxe detail',
    afterAlt: 'Same truck after Luxe Package — mirror-finish paint, dressed trim, spotless wheels',
    microCta: 'Book the Luxe',
    tag: 'Luxe Package',
  },
  {
    question: 'Why does pricing vary by vehicle type?',
    answer:
      "A compact sedan and a full-size pickup truck are very different jobs. More surface area, more carpet, more panels, more time. The price ranges listed (e.g. $175–$235 for the Basic Bundle) reflect that difference — sedans and coupes land at the lower end, while SUVs, trucks, and vans are at the higher end. If you're unsure where your vehicle falls, just DM us a photo and we'll give you an exact quote before you commit to anything.",
    beforeSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_13fdc1cab-1772167306434.png',
    afterSrc: 'https://img.rocket.new/generatedImages/rocket_gen_img_13fdc1cab-1772167306434.png',
    beforeAlt: 'Large SUV with dirty exterior and dusty interior before mobile detail',
    afterAlt: 'Same large SUV after full detail — clean paint, dressed tires, spotless interior',
    microCta: 'Get My Quote',
    tag: 'Pricing',
  },
];

export default function HomePage() {
  const [resultsOpen, setResultsOpen] = useState(false);

  useEffect(() => {
    trackPageView('/home');
  }, []);

  return (
    <div style={{ background: '#050510', minHeight: '100vh' }}>
      <Header />

      {/* ── Launch Date + Discount Announcement Banner ── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0d0d2b 0%, #111130 100%)',
          borderBottom: '1px solid rgba(123,47,255,0.4)',
          padding: '12px clamp(12px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Glow */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(123,47,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 relative" style={{ zIndex: 1 }}>
          {/* Launch date pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,0,255,0.1)', border: '1px solid rgba(255,0,255,0.4)', padding: '6px 12px', boxShadow: '0 0 16px rgba(255,0,255,0.15)' }}>
            <span style={{ color: '#ff00ff', fontSize: '12px', textShadow: '0 0 8px rgba(255,0,255,0.6)' }}>⏳</span>
            <span style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#ff00ff', textShadow: '0 0 8px rgba(255,0,255,0.4)', whiteSpace: 'nowrap' }}>
              Launching Late Sept – Mid Oct 2026
            </span>
          </div>
          <span style={{ color: 'rgba(0,245,255,0.3)', fontSize: '16px' }} className="hidden sm:block">|</span>
          {/* Discount pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(123,47,255,0.12)', border: '1px solid rgba(123,47,255,0.5)', padding: '6px 12px', boxShadow: '0 0 16px rgba(123,47,255,0.2)' }}>
            <span style={{ color: '#7b2fff', fontSize: '12px', textShadow: '0 0 8px rgba(123,47,255,0.6)' }}>★</span>
            <span style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#e0e8ff', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#7b2fff', textShadow: '0 0 10px rgba(123,47,255,0.6)' }}>15% OFF</span> — Waitlist &amp; Advance Bookings
            </span>
          </div>
        </div>
      </div>

      {/* ── Ticker / Marquee bar ── */}
      <div
        style={{
          background: 'linear-gradient(90deg, #7b2fff, #00f5ff, #ff00ff, #7b2fff)',
          backgroundSize: '300% 100%',
          borderBottom: '1px solid rgba(0,245,255,0.3)',
          overflow: 'hidden',
          padding: '8px 0',
        }}
      >
        <div className="z-ticker-track">
          {[
            '🚗 Mobile Detailing',
            '★ Mesquite, NV',
            '🚗 We Come to You',
            '★ Water Access Required',
            '🚗 3 Packages Available',
            '★ Launching Sept–Oct 2026',
            '🚗 Las Vegas to St. George UT',
            '★ DM for a Quote',
            '🚗 Mobile Detailing',
            '★ Mesquite, NV',
            '🚗 We Come to You',
            '★ Water Access Required',
            '🚗 3 Packages Available',
            '★ Launching Sept–Oct 2026',
            '🚗 Las Vegas to St. George UT',
            '★ DM for a Quote',
          ]?.map((item, i) => (
            <span
              key={i}
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#050510',
                padding: '0 24px',
                whiteSpace: 'nowrap',
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
      {/* ── Hero Banner ── */}
      <div
        className="cp-scanlines"
        style={{
          background: 'linear-gradient(135deg, #050510 0%, #0d0d2b 50%, #050510 100%)',
          padding: 'clamp(36px, 8vw, 96px) clamp(16px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Cyberpunk grid background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,245,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,245,255,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />
        {/* Glow orbs */}
        <div style={{ position: 'absolute', top: '20%', left: '10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(123,47,255,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(0,245,255,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 2 }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-8">
            <div className="flex-1">
              {/* Tag */}
              <div
                className="inline-block px-3 py-1 mb-4"
                style={{ background: 'rgba(0,245,255,0.1)', border: '1px solid #00f5ff', boxShadow: '0 0 12px rgba(0,245,255,0.2)' }}
              >
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    color: '#00f5ff',
                    textShadow: '0 0 8px rgba(0,245,255,0.6)',
                  }}
                >
                  🚗 Now Booking · Mesquite, NV
                </span>
              </div>

              {/* Main headline */}
              <h1
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(52px, 14vw, 120px)',
                  textTransform: 'uppercase',
                  lineHeight: 0.88,
                  letterSpacing: '-0.01em',
                  color: '#e0e8ff',
                  marginBottom: '16px',
                  textShadow: '0 0 40px rgba(0,245,255,0.2)',
                }}
              >
                VERTEX
                <br />
                <span
                  style={{
                    color: '#00f5ff',
                    WebkitTextStroke: '2px rgba(0,245,255,0.4)',
                    textShadow: '0 0 30px rgba(0,245,255,0.6), 0 0 60px rgba(0,245,255,0.3)',
                  }}
                >
                  PREMIUM
                </span>
              </h1>

              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(16px, 3vw, 28px)',
                  textTransform: 'uppercase',
                  color: '#a0b0d8',
                  letterSpacing: '0.06em',
                  marginBottom: '8px',
                }}
              >
                Premium Mobile Detailing
              </p>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 2vw, 18px)',
                  textTransform: 'uppercase',
                  color: '#7b8ab8',
                  letterSpacing: '0.1em',
                }}
              >
                Las Vegas, NV → Mesquite, NV → St. George, UT
              </p>
            </div>

            {/* CTA block */}
            <div
              style={{
                background: 'rgba(13,13,43,0.9)',
                border: '1px solid rgba(0,245,255,0.3)',
                padding: '20px',
                width: '100%',
                maxWidth: '100%',
                boxShadow: '0 0 30px rgba(0,245,255,0.1), inset 0 0 30px rgba(0,245,255,0.03)',
              }}
              className="md:min-w-[240px] md:max-w-[280px]"
            >
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#ff00ff',
                  marginBottom: '4px',
                  textShadow: '0 0 8px rgba(255,0,255,0.5)',
                }}
              >
                Launching Sept–Oct 2026
              </p>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '20px',
                  textTransform: 'uppercase',
                  color: '#e0e8ff',
                  lineHeight: 1.1,
                  marginBottom: '16px',
                }}
              >
                We Come to You.
                <br />
                You Just Need Water.
              </p>
              <div className="flex gap-3 md:flex-col">
                <a
                  href="/waitlist"
                  className="z-btn-yellow flex-1 md:w-full flex items-center justify-center"
                  style={{ minHeight: '48px', fontSize: '14px', textDecoration: 'none' }}
                >
                  Join Waitlist
                </a>
                <a
                  href="/home#quote"
                  className="flex-1 md:w-full flex items-center justify-center"
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#00f5ff',
                    border: '1px solid rgba(0,245,255,0.4)',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    minHeight: '48px',
                    background: 'rgba(0,245,255,0.05)',
                  }}
                >
                  Get a Quote →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── Stats bar ── */}
      <div
        style={{
          background: '#0d0d2b',
          borderBottom: '1px solid #1a1a4e',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {[
              { value: '3', label: 'Packages', sub: 'Basic → Luxe' },
              { value: '100%', label: 'Mobile', sub: 'We Come to You' },
              { value: 'NV', label: 'Mesquite', sub: 'Home Base' },
              { value: '2.5–6h', label: 'Duration', sub: 'By Package' },
            ]?.map((stat, i) => (
              <div
                key={stat?.label}
                style={{
                  padding: '16px 12px',
                  borderRight: (i === 1 || i === 3) ? 'none' : '1px solid #1a1a4e',
                  borderBottom: i < 2 ? '1px solid #1a1a4e' : 'none',
                  textAlign: 'center',
                }}
                className="md:border-b-0"
              >
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: 'clamp(24px, 5vw, 36px)',
                    color: '#00f5ff',
                    lineHeight: 1,
                    marginBottom: '4px',
                    textShadow: '0 0 16px rgba(0,245,255,0.5)',
                  }}
                >
                  {stat?.value}
                </div>
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#e0e8ff',
                  }}
                >
                  {stat?.label}
                </div>
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#7b8ab8',
                    marginTop: '2px',
                  }}
                >
                  {stat?.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* ── Main content ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <main className="pb-24 md:pb-12">

          {/* ── Service Area section ── */}
          <div id="service-area" className="py-10 md:py-14">
            {/* Section label */}
            <div
              className="inline-block px-3 py-1 mb-4"
              style={{ background: 'rgba(123,47,255,0.15)', border: '1px solid #7b2fff', boxShadow: '0 0 10px rgba(123,47,255,0.2)' }}
            >
              <span
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: '#7b2fff',
                  textShadow: '0 0 8px rgba(123,47,255,0.6)',
                }}
              >
                Service Area
              </span>
            </div>

            <h2
              className="z-section-heading"
              style={{ fontSize: 'clamp(28px, 5vw, 48px)', lineHeight: 0.95, marginBottom: '16px' }}
            >
              We Travel the Corridor
            </h2>

            <p style={{ fontSize: '14px', color: '#a0b0d8', marginBottom: '24px', maxWidth: '560px' }}>
              Based in Mesquite, NV — we serve the entire Las Vegas to St. George corridor. Travel fee may apply for locations outside Mesquite.
            </p>

            {/* Route display */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-0">
              {[
                { city: 'Las Vegas', state: 'NV', note: 'South End', featured: false },
                { city: 'Mesquite', state: 'NV', note: 'Home Base', featured: true },
                { city: 'St. George', state: 'UT', note: 'North End', featured: false },
              ]?.map((stop, i) => (
                <React.Fragment key={stop?.city}>
                  <div
                    style={{
                      background: stop?.featured ? 'rgba(0,245,255,0.08)' : '#0d0d2b',
                      border: stop?.featured ? '1px solid #00f5ff' : '1px solid #1a1a4e',
                      padding: '14px 20px',
                      textAlign: 'center',
                      flex: stop?.featured ? '1.2' : '1',
                      boxShadow: stop?.featured ? '0 0 20px rgba(0,245,255,0.15)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: 'clamp(16px, 3vw, 22px)',
                        textTransform: 'uppercase',
                        color: stop?.featured ? '#00f5ff' : '#e0e8ff',
                        lineHeight: 1,
                        marginBottom: '4px',
                        textShadow: stop?.featured ? '0 0 12px rgba(0,245,255,0.5)' : 'none',
                      }}
                    >
                      {stop?.city}, {stop?.state}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 700,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        color: stop?.featured ? '#a0b0d8' : '#7b8ab8',
                      }}
                    >
                      {stop?.note}
                    </div>
                  </div>
                  {i < 2 && (
                    <div
                      style={{
                        background: '#1a1a4e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        minWidth: '36px',
                        minHeight: '36px',
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: '18px',
                        color: '#00f5ff',
                        textShadow: '0 0 8px rgba(0,245,255,0.6)',
                      }}
                    >
                      →
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ── Vision callout ── */}
          <div
            style={{
              background: '#0d0d2b',
              border: '1px solid #1a1a4e',
              padding: '24px',
              marginBottom: '32px',
              position: 'relative',
            }}
          >
            {/* Cyan left accent */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '4px',
                height: '100%',
                background: 'linear-gradient(180deg, #00f5ff, #7b2fff)',
                boxShadow: '0 0 12px rgba(0,245,255,0.4)',
              }}
            />
            <div className="pl-4">
              <div
                className="inline-block px-3 py-1 mb-3"
                style={{ background: 'rgba(123,47,255,0.15)', border: '1px solid #7b2fff' }}
              >
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#7b2fff',
                  }}
                >
                  The Road Ahead
                </span>
              </div>
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-1">
                  <h3
                    style={{
                      fontFamily: 'Barlow Condensed, Arial, sans-serif',
                      fontWeight: 900,
                      fontSize: '28px',
                      textTransform: 'uppercase',
                      color: '#e0e8ff',
                      lineHeight: 1.05,
                      marginBottom: '8px',
                    }}
                  >
                    New Business. Big Vision.
                  </h3>
                  <p style={{ fontSize: '14px', color: '#a0b0d8', lineHeight: 1.7 }}>
                    Vertex Premium is a brand-new operation — launching in approximately{' '}
                    <strong style={{ color: '#e0e8ff' }}>September–October 2026</strong>. We're starting lean and mobile, but this is just the beginning. The long-term vision is a full-service shop: premium detailing bays, performance parts, and in-house tuning — all under one roof.
                  </p>
                </div>
                <div className="flex flex-col gap-2 md:w-52 shrink-0">
                  {[
                    { label: 'Launching', value: 'Sept – Oct 2026' },
                    { label: 'Phase 1', value: 'Mobile Detailing' },
                    { label: 'Future Vision', value: 'Full Shop + Tuning' },
                  ]?.map((item) => (
                    <div
                      key={item?.label}
                      style={{
                        background: '#111130',
                        border: '1px solid #1a1a4e',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <span style={{ color: '#00f5ff', fontSize: '14px', background: 'rgba(0,245,255,0.1)', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(0,245,255,0.3)' }}>★</span>
                      <div>
                        <p
                          style={{
                            fontFamily: 'Barlow Condensed, Arial, sans-serif',
                            fontWeight: 700,
                            fontSize: '9px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: '#7b8ab8',
                          }}
                        >
                          {item?.label}
                        </p>
                        <p
                          style={{
                            fontFamily: 'Barlow Condensed, Arial, sans-serif',
                            fontWeight: 900,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            color: '#e0e8ff',
                          }}
                        >
                          {item?.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Services section ── */}
          <div className="z-divider mb-8" />
          <ServicesSection />

          {/* ── CTA block ── */}
          <CtaBlock />

          {/* ── FAQ section ── */}
          <div id="faq" className="pt-10 md:pt-14">
            <div
              className="inline-block px-3 py-1 mb-4"
              style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.4)' }}
            >
              <span
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: '#00f5ff',
                }}
              >
                Common Questions
              </span>
            </div>
            <h2
              className="z-section-heading"
              style={{ fontSize: 'clamp(28px, 5vw, 48px)', lineHeight: 0.95, marginBottom: '4px' }}
            >
              Everything You Want to Know
            </h2>
            <p
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(16px, 2.5vw, 22px)',
                textTransform: 'uppercase',
                color: '#7b8ab8',
                letterSpacing: '0.04em',
                marginBottom: '24px',
              }}
            >
              Before You Book.
            </p>
          </div>

          <div className="z-divider mb-2" />

          {/* FAQ items with CTA blocks interspersed */}
          {faqItems?.map((item, i) => (
            <React.Fragment key={i}>
              <FaqZigzag item={item} index={i} onCta={() => {}} />
              {(i + 1) % 2 === 0 && i < faqItems?.length - 1 && (
                <>
                  <div className="z-divider-light my-2" />
                  <CtaBlock />
                  <div className="z-divider-light my-2" />
                </>
              )}
            </React.Fragment>
          ))}

          {/* ── Photo quote form ── */}
          <div id="quote" className="z-divider my-8" />
          <PhotoQuoteForm />

          {/* ── Waitlist ── */}
          <div className="z-divider my-8" />
          <WaitlistSection />

          {/* ── Trust strip ── */}
          <div
            style={{
              background: '#0d0d2b',
              border: '1px solid #1a1a4e',
              padding: '16px',
              marginBottom: '48px',
            }}
          >
            <div className="flex flex-wrap items-center justify-center gap-x-0 gap-y-2">
            {[
              '🚗 Mobile Service · Mesquite NV',
              '★ Las Vegas → St. George UT',
              '🚗 Full Interior + Exterior',
              '★ Water Access Required',
              '🚗 3 Package Options',
            ]?.map((badge, i) => (
              <React.Fragment key={badge}>
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#a0b0d8',
                    padding: '4px 12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {badge}
                </span>
                {i < 4 && (
                  <span className="hidden sm:inline" style={{ color: '#1a1a4e', fontSize: '16px' }}>|</span>
                )}
              </React.Fragment>
            ))}
            </div>
          </div>
        </main>
      </div>
      <Footer />
      <StickyMobileBar />

      {/* ── Results & Reviews floating trigger ── */}
      <button
        onClick={() => setResultsOpen(true)}
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'linear-gradient(180deg, #0d0d2b 0%, #111130 100%)',
          border: '1px solid rgba(0,245,255,0.35)',
          borderRight: 'none',
          padding: '12px 8px',
          cursor: 'pointer',
          zIndex: 9990,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '5px',
          boxShadow: '-4px 0 24px rgba(0,245,255,0.12)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(180deg, rgba(0,245,255,0.1) 0%, rgba(123,47,255,0.1) 100%)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '-4px 0 32px rgba(0,245,255,0.25)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(180deg, #0d0d2b 0%, #111130 100%)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '-4px 0 24px rgba(0,245,255,0.12)';
        }}
        aria-label="View before/after results and customer reviews"
      >
        <span style={{ color: '#00f5ff', fontSize: '14px', textShadow: '0 0 10px rgba(0,245,255,0.6)' }}>◈</span>
        {['R','E','S','U','L','T','S'].map((char, i) => (
          <span
            key={i}
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#00f5ff',
              textShadow: '0 0 8px rgba(0,245,255,0.5)',
              lineHeight: 1,
            }}
          >
            {char}
          </span>
        ))}
        <span style={{ color: '#7b2fff', fontSize: '10px', textShadow: '0 0 8px rgba(123,47,255,0.6)' }}>★</span>
      </button>

      <ResultsPopup isOpen={resultsOpen} onClose={() => setResultsOpen(false)} />
    </div>
  );
}
